import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, MapPin, ChevronRight, Home, Trash2 } from 'lucide-react';
import { edificiosApi, unidadesApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/common/StatusBadge';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const initialFormData = {
  nombre: '',
  direccion: '',
  descripcion: '',
  estado: 'activo',
};

export const AnalistaEdificios = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const basePath = user?.rol === 'comercial' ? '/comercial' : '/analista';
  const [edificios, setEdificios] = useState([]);
  const [unidadesCounts, setUnidadesCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [edificiosRes, unidadesRes] = await Promise.all([
        edificiosApi.list(),
        unidadesApi.list(),
      ]);
      
      setEdificios(edificiosRes.data);
      
      // Count unidades per edificio
      const counts = {};
      unidadesRes.data.forEach(u => {
        counts[u.edificio_id] = (counts[u.edificio_id] || 0) + 1;
      });
      setUnidadesCounts(counts);
    } catch (err) {
      console.error('Error fetching edificios:', err);
      setError('Error al cargar los edificios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await edificiosApi.create(formData);
      toast.success('Edificio creado');
      setIsDialogOpen(false);
      // Navigate to the new edificio
      navigate(`${basePath}/edificio/${response.data.edificio_id}`);
    } catch (err) {
      console.error('Error saving edificio:', err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdificioClick = (edificioId) => {
    navigate(`${basePath}/edificio/${edificioId}`);
  };

  const handleDeleteClick = (e, edificio) => {
    e.stopPropagation();
    setDeleteTarget(edificio);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      await edificiosApi.delete(deleteTarget.edificio_id);
      toast.success('Edificio eliminado');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting edificio:', err);
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState message="Cargando edificios..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  // Vista simplificada para comercial: solo nombres en cuadros, centrados
  if (user?.rol === 'comercial') {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6">
        {edificios.length === 0 ? (
          <p className="text-slate-500">No hay edificios disponibles.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-5xl">
            {edificios.map((edificio) => (
              <button
                key={edificio.edificio_id}
                onClick={() => handleEdificioClick(edificio.edificio_id)}
                className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl p-8 hover:border-emerald-400 hover:shadow-md transition-all text-left"
                data-testid={`edificio-card-${edificio.edificio_id}`}
              >
                <span className="block text-3xl md:text-4xl font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">
                  {edificio.nombre}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Mis Edificios</h1>
          <p className="text-slate-500 mt-1">Selecciona un edificio para gestionar sus unidades y carga mensual</p>
        </div>
        
        <Button onClick={handleOpenCreate} data-testid="create-edificio-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Edificio
        </Button>
      </div>

      {/* List */}
      {edificios.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Sin edificios"
          description="Aún no hay edificios registrados. Crea tu primer edificio para comenzar."
          action={
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primer edificio
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {edificios.map((edificio) => {
            const unidadesCount = unidadesCounts[edificio.edificio_id] || 0;
            
            return (
              <div
                key={edificio.edificio_id}
                onClick={() => handleEdificioClick(edificio.edificio_id)}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:border-[#1B4D3E] hover:shadow-md transition-all duration-200 cursor-pointer group relative"
                data-testid={`edificio-card-${edificio.edificio_id}`}
              >
                {/* Delete button */}
                <button
                  onClick={(e) => handleDeleteClick(e, edificio)}
                  className="absolute top-3 right-3 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  title="Eliminar edificio"
                  data-testid={`delete-edificio-${edificio.edificio_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center group-hover:bg-[#1B4D3E]/20 transition-colors">
                    <Building2 className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#1B4D3E] transition-colors" />
                </div>
                
                <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#1B4D3E] transition-colors">
                  {edificio.nombre}
                </h3>
                
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span className="truncate">{edificio.direccion}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      {unidadesCount} {unidadesCount === 1 ? 'unidad' : 'unidades'}
                    </span>
                  </div>
                  <StatusBadge status={edificio.estado} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Edificio</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Torre Centro"
                required
                data-testid="input-nombre"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección *</Label>
              <Input
                id="direccion"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Ej: San Martín 1234, Mendoza"
                required
                data-testid="input-direccion"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional del edificio"
                rows={3}
                data-testid="input-descripcion"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="submit-edificio">
                {saving ? 'Creando...' : 'Crear y Configurar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar edificio?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás por eliminar el edificio <strong>{deleteTarget?.nombre}</strong>.
              {unidadesCounts[deleteTarget?.edificio_id] > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Este edificio tiene {unidadesCounts[deleteTarget?.edificio_id]} unidades asociadas que también serán eliminadas.
                </span>
              )}
              <span className="block mt-2">
                Esta acción no se puede deshacer.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AnalistaEdificios;
